"use client";

import { FC, PropsWithChildren, useContext } from "react";
import classNames from "classnames";
import { useCollapseHeight, useRuntimeConfig } from "@finbackoffice/site-core";
import { useTranslation } from "react-i18next";
import { FooterContext } from "components/footer/Footer";
import Translate from "components/base/translate/Translate";
import Img from "components/base/img/Img";
import { AssetsDirectory } from "@finbackoffice/enums";
import ErrorBoundary from "components/base/error-boundary/ErrorBoundary";
import LicenseLogo from "components/footer/license-logo/LicenseLogo";
import PromotionsDefaultList from "components/bonuses/promotions/list/PromotionsDefaultList";
import Link from "next/link";
import styles from "./flat.module.sass";

type IProps = {
    title: string;
};

const CollapsingBlok: FC<IProps & PropsWithChildren> = ({ children, title }) => {
    const { opened, animating, headerRef, containerRef } = useCollapseHeight(false, 300);

    return (
        <div className={styles.item}>
            <div ref={headerRef} className={classNames(opened && styles.opened)}>
                {title}
                <i className={styles.arrow} />
            </div>
            {(opened || animating) && <nav ref={containerRef}>{children}</nav>}
        </div>
    );
};

const Flat: FC = () => {
    const { t } = useTranslation();
    const COMMON_SITE_CONFIGS = useRuntimeConfig("COMMON_SITE_CONFIGS");
    const VERSION = useRuntimeConfig("VERSION");

    const {
        enabledPoliciesConfig,
        renderPromotionsLink,
        renderPoliciesLink,
        renderSportLinks,
        renderCasinoLinks,
        renderCurrenciesSection,
        renderSocialLinksSection,
        renderExchangeRateSection,
    } = useContext(FooterContext);

    return (
        <footer className={styles.footer}>
            {COMMON_SITE_CONFIGS.contentVerticals.sport && (
                <CollapsingBlok title={t("footer_sports")}>{renderSportLinks}</CollapsingBlok>
            )}

            {(COMMON_SITE_CONFIGS.contentVerticals.livecasino ||
                COMMON_SITE_CONFIGS.contentVerticals.slots) && (
                <CollapsingBlok title={t("footer_casino")}>{renderCasinoLinks}</CollapsingBlok>
            )}

            <CollapsingBlok title={t("footer_bonuses")}>
                {renderPromotionsLink}
                <PromotionsDefaultList />
            </CollapsingBlok>
            <CollapsingBlok title={t("footer_help_center")}>
                {enabledPoliciesConfig.map(renderPoliciesLink)}
            </CollapsingBlok>

            {COMMON_SITE_CONFIGS.license.enable && (
                <div className={styles.licenseLogo}>
                    <ErrorBoundary name={LicenseLogo.name}>
                        <LicenseLogo />
                    </ErrorBoundary>
                </div>
            )}
            <Translate
                tid="footer_copyright_text"
                dangerous
                replace={{ app_name: COMMON_SITE_CONFIGS.appName }}>
                <span className={styles.txt} />
            </Translate>
            {renderCurrenciesSection}
            {renderSocialLinksSection}
            {renderExchangeRateSection}
            <Link href="/" className={styles.logo}>
                <Img
                    source={`/${AssetsDirectory.Mobile}/logo.svg`}
                    alt={`${COMMON_SITE_CONFIGS.appName} logo`}
                    title={`${COMMON_SITE_CONFIGS.appName} logo`}
                    width={0}
                    height={0}
                    style={{ width: "100%", height: "auto" }}
                    prefixAppName
                />
            </Link>
            <p className={styles.footerSite}>{`© ${new Date().getFullYear()} ${
                COMMON_SITE_CONFIGS.appName
            }`}</p>
            <p className={styles.version}>
                <Translate tid="footer_version" replace={{ version: VERSION }} />
            </p>
        </footer>
    );
};

export default Flat;
